import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const SkillLegend = ({ isVisible = true }) => {
  const legendItems = [
    {
      category: 'technical',
      name: 'Technical Skills',
      icon: 'Code',
      shape: 'polygon(50% 0%, 0% 100%, 100% 100%)',
      gradient: 'from-accent to-blue-400',
      description: 'Programming, frameworks, and development tools'
    },
    {
      category: 'creative',
      name: 'Creative Skills',
      icon: 'Palette',
      shape: 'circle(50%)',
      gradient: 'from-cosmic-energy to-pink-400',
      description: 'Design, visual arts, and creative direction'
    },
    {
      category: 'hybrid',
      name: 'Hybrid Skills',
      icon: 'Zap',
      shape: 'polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)',
      gradient: 'from-cosmic-cta to-purple-400',
      description: 'Cross-disciplinary expertise combining tech and creativity'
    }
  ];

  const connectionTypes = [
    {
      type: 'synergy',
      name: 'Synergy',
      color: 'rgba(0, 212, 255, 0.6)',
      strokeStyle: 'solid',
      description: 'Skills that enhance each other'
    },
    {
      type: 'enhancement',
      name: 'Enhancement',
      color: 'rgba(255, 107, 53, 0.5)',
      strokeStyle: 'dashed',
      description: 'Skills that improve others'
    },
    {
      type: 'foundation',
      name: 'Foundation',
      color: 'rgba(255, 51, 102, 0.4)',
      strokeStyle: 'dotted',
      description: 'Core skills supporting others'
    }
  ];

  if (!isVisible) return null;

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: 1.5 }}
      className="fixed top-24 left-6 z-30 max-w-xs"
    >
      <div className="glass-panel rounded-xl p-4 shadow-cosmic-lg">
        {/* Header */}
        <div className="flex items-center space-x-2 mb-4">
          <Icon name="Map" size={20} className="text-accent" />
          <h3 className="font-headline text-lg font-semibold text-foreground">
            Constellation Guide
          </h3>
        </div>

        {/* Skill Categories */}
        <div className="space-y-3 mb-6">
          <h4 className="font-body text-sm font-medium text-muted-foreground uppercase tracking-wide">
            Skill Categories
          </h4>
          {legendItems?.map((item) => (
            <div key={item?.category} className="flex items-center space-x-3">
              <div className="relative">
                <div 
                  className={`w-6 h-6 bg-gradient-to-r ${item?.gradient} flex items-center justify-center`}
                  style={{ clipPath: item?.shape }}
                >
                  <Icon name={item?.icon} size={12} className="text-background" />
                </div>
              </div>
              <div>
                <p className="font-body text-sm font-medium text-foreground">
                  {item?.name}
                </p>
                <p className="font-body text-xs text-muted-foreground">
                  {item?.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Connection Types */}
        <div className="space-y-3 mb-6">
          <h4 className="font-body text-sm font-medium text-muted-foreground uppercase tracking-wide">
            Connections
          </h4>
          {connectionTypes?.map((connection) => (
            <div key={connection?.type} className="flex items-center space-x-3">
              <div className="w-6 h-1 relative">
                <div 
                  className="w-full h-full"
                  style={{ 
                    backgroundColor: connection?.color,
                    borderStyle: connection?.strokeStyle === 'dashed' ? 'dashed' : 'solid',
                    borderWidth: connection?.strokeStyle === 'dotted' ? '1px' : '0',
                    borderColor: connection?.color
                  }}
                />
              </div>
              <div>
                <p className="font-body text-sm font-medium text-foreground">
                  {connection?.name}
                </p>
                <p className="font-body text-xs text-muted-foreground">
                  {connection?.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Skill Levels */}
        <div className="space-y-3">
          <h4 className="font-body text-sm font-medium text-muted-foreground uppercase tracking-wide">
            Proficiency Levels
          </h4>
          <div className="space-y-2">
            {[
              { level: 5, name: 'Expert', description: 'Mastery level expertise' },
              { level: 4, name: 'Advanced', description: 'Professional proficiency' },
              { level: 3, name: 'Proficient', description: 'Solid working knowledge' },
              { level: 2, name: 'Intermediate', description: 'Basic competency' },
              { level: 1, name: 'Beginner', description: 'Learning fundamentals' }
            ]?.map((item) => (
              <div key={item?.level} className="flex items-center space-x-3">
                <div className="flex space-x-1">
                  {[...Array(5)]?.map((_, i) => (
                    <div
                      key={i}
                      className={`w-1.5 h-1.5 rounded-full ${
                        i < item?.level ? 'bg-accent' : 'bg-muted'
                      }`}
                    />
                  ))}
                </div>
                <div>
                  <p className="font-body text-xs font-medium text-foreground">
                    {item?.name}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Special Indicators */}
        <div className="mt-6 pt-4 border-t border-border/20">
          <h4 className="font-body text-sm font-medium text-muted-foreground uppercase tracking-wide mb-3">
            Special Indicators
          </h4>
          <div className="space-y-2">
            <div className="flex items-center space-x-3">
              <div className="w-4 h-4 border-2 border-cosmic-cta rounded-full animate-ping opacity-75" />
              <p className="font-body text-xs text-muted-foreground">
                Recently acquired skills
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-4 h-4 bg-accent rounded-full animate-pulse-glow" />
              <p className="font-body text-xs text-muted-foreground">
                Core competencies
              </p>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default SkillLegend;